import { ReactNode, useContext } from "react";
import {
  hasOneOfRoles,
  ROLE_ADMIN,
  ROLE_ADVISOR,
  ROLE_SUPERADMIN,
} from "../../common/userUtils";
import { CurrentUserContext } from "components/context/RequireAuth";
import { Box, Grid, Typography } from "@mui/material";
import LinkRouter from "components/navigation/LinkRouter";
import useIsMobile from "components/hooks/useIsMobile";
import Breadcrumbs from "components/common/Breadcrumbs";
import { CurrentUser } from "protogen/auth_pb";
import {
  Bell,
  BookHeart,
  BookUser,
  ContactRound,
  FlaskConical,
  FolderKanban,
  MailQuestion,
  UserCog,
  Zap,
  VenetianMask,
  Videotape,
  ScissorsLineDashed,
  SquareActivity,
  Lightbulb,
  Landmark,
  ListTodo,
  TrendingUpDown,
  FileSpreadsheet,
  Rows3,
  Sparkles,
  RefreshCcw,
  Tag,
  LibraryBig,
  Receipt,
  Award,
  Clock,
  Star,
  Bot,
  AlertTriangle,
  BookOpen,
  FileDown,
} from "lucide-react";

// You can replace these types with the actual types you are using.
type Tool = {
  path: string;
  name: string;
  acceptableRoles: string[];
  icon: ReactNode;
  description?: string;
  badged?: boolean;
};

const tools: Tool[] = [
  // Should be dynamic at some point.
  // Advisor tools
  {
    path: "advisor-directory",
    name: "Advisor directory",
    acceptableRoles: [ROLE_ADVISOR],
    icon: <ContactRound size={32} color="#198282" />,
    description:
      "Discover and meet other FAs in your area with different specialties.",
  },
  {
    path: "feature-switches",
    name: "Faye labs",
    acceptableRoles: [ROLE_ADVISOR],
    icon: <FlaskConical size={32} color="#198282" />,
    description: "Test out new features before they're published for everyone.",
  },
  {
    path: "reports",
    name: "Reports",
    acceptableRoles: [ROLE_ADVISOR],
    icon: <FolderKanban size={32} color="#198282" />,
    description:
      "See statistics on hours spent on tasks, task counts, and more.",
  },
  {
    path: "family-notifications",
    name: "Family Notifications",
    acceptableRoles: [ROLE_ADVISOR],
    icon: <Bell size={32} color="#198282" />,
    description: "Customize which families you receive notifications for.",
  },
  {
    path: "identity-lookup",
    name: "Identity Lookup",
    acceptableRoles: [ROLE_ADMIN],
    icon: <ContactRound size={32} color="#198282" />,
  },
  {
    path: "url-shortener",
    name: "Url Shortener",
    acceptableRoles: [ROLE_ADVISOR],
    icon: <ScissorsLineDashed size={32} color="#198282" />,
    description: "Create shortened Faye branded urls.",
  },
  {
    path: "/task-discovery",
    name: "Task discovery",
    acceptableRoles: [ROLE_ADVISOR],
    icon: <Lightbulb size={32} color="#198282" />,
    description: "See the tasks we suggest to clients.",
  },
  {
    path: "/prompts",
    name: "Magic Documents",
    acceptableRoles: [ROLE_ADVISOR],
    icon: <Sparkles size={32} color="#198282" />,
    description:
      "Use AI to generate personalized inspiration for your families.",
  },
  {
    path: "agents/playbooks",
    name: "Playbooks",
    acceptableRoles: [ROLE_ADVISOR],
    icon: <LibraryBig size={32} color="#198282" />,
    description: "Create and manage AI playbooks for task agents.",
  },
  {
    path: "family-invoices",
    name: "Family Invoices",
    acceptableRoles: [ROLE_ADVISOR],
    icon: <Receipt size={32} color="#198282" />,
    description: "View invoice history and payout details for families.",
  },
  // Admin tools
  {
    path: "audit-log",
    name: "Audit Log",
    acceptableRoles: [ROLE_ADMIN],
    icon: <Videotape size={32} color="#198282" />,
  },
  {
    path: "advisor-administration",
    name: "Manage advisors",
    acceptableRoles: [ROLE_ADMIN],
    icon: <UserCog size={32} color="#198282" />,
  },
  {
    path: "advisor-applications",
    name: "Advisor Applications",
    acceptableRoles: [ROLE_ADMIN],
    icon: <Rows3 size={32} color="#198282" />,
  },
  {
    path: "family-directory",
    name: "Family directory",
    acceptableRoles: [ROLE_ADMIN],
    icon: <BookHeart size={32} color="#198282" />,
  },
  {
    path: "family-engagement",
    name: "Family engagement",
    acceptableRoles: [ROLE_ADMIN],
    icon: <SquareActivity size={32} color="#198282" />,
  },
  {
    path: "family-context",
    name: "Family Context",
    acceptableRoles: [ROLE_ADMIN],
    icon: <FileSpreadsheet size={32} color="#198282" />,
  },
  {
    path: "impersonate-user",
    name: "Impersonate user",
    acceptableRoles: [ROLE_ADMIN],
    icon: <VenetianMask size={32} color="#198282" />,
  },
  {
    path: "manage-roles",
    name: "Manage roles",
    acceptableRoles: [ROLE_SUPERADMIN],
    icon: <BookUser size={32} color="#198282" />,
  },
  {
    path: "resources-cms",
    name: "Resources & Help CMS",
    acceptableRoles: [ROLE_ADMIN],
    icon: <BookOpen size={32} color="#198282" />,
  },
  {
    path: "unassigned-emails",
    name: "Unassigned emails",
    acceptableRoles: [ROLE_ADMIN],
    icon: <MailQuestion size={32} color="#198282" />,
  },
  {
    path: "task-inspiration",
    name: "Task inspiration",
    acceptableRoles: [ROLE_ADMIN],
    icon: <ListTodo size={32} color="#198282" />,
  },
  {
    path: "advisor-payments",
    name: "Advisor Payments",
    acceptableRoles: [ROLE_ADMIN], // This should be ROLE_PAYMENTS
    icon: <Landmark size={32} color="#198282" />,
  },
  {
    path: "pipeline-management",
    name: "Pipeline Management",
    acceptableRoles: [ROLE_ADMIN],
    icon: <TrendingUpDown size={32} color="#198282" />,
  },
  {
    path: "rematching",
    name: "Rematching",
    acceptableRoles: [ROLE_ADMIN],
    icon: <RefreshCcw size={32} color="#198282" />,
  },
  {
    path: "client-ai-tester",
    name: "Client AI Tester",
    acceptableRoles: [ROLE_ADMIN],
    icon: <Sparkles size={32} color="#198282" />,
  },
  {
    path: "pipeline-metadata",
    name: "Pipeline Metadata",
    acceptableRoles: [ROLE_ADMIN],
    icon: <Tag size={32} color="#198282" />,
  },
  {
    path: "badge-management",
    name: "Badge Management",
    acceptableRoles: [ROLE_ADMIN],
    icon: <Award size={32} color="#198282" />,
  },
  {
    path: "schedule-management",
    name: "Schedule Management",
    acceptableRoles: [ROLE_ADMIN],
    icon: <Clock size={32} color="#198282" />,
  },
  {
    path: "delegations",
    name: "Delegations",
    acceptableRoles: [ROLE_ADMIN],
    icon: <Zap size={32} color="#198282" />,
    // description: "Manage delegated phone calls and tasks.",
  },
  {
    path: "create-lightning-task",
    name: "Create Lightning Task",
    acceptableRoles: [ROLE_ADMIN],
    icon: <Zap size={32} color="#198282" />,
  },
  {
    path: "reviews",
    name: "Reviews",
    acceptableRoles: [ROLE_ADMIN],
    icon: <Star size={32} color="#198282" />,
  },
  {
    path: "bot-conversations",
    name: "Bot Conversations",
    acceptableRoles: [ROLE_ADMIN],
    icon: <Bot size={32} color="#198282" />,
  },
  {
    path: "agent-errors",
    name: "Agent Errors",
    acceptableRoles: [ROLE_ADMIN],
    icon: <AlertTriangle size={32} color="#198282" />,
  },
  {
    path: "digital-subscription-playbooks",
    name: "Digital Subscription Playbooks",
    acceptableRoles: [ROLE_ADMIN],
    icon: <BookOpen size={32} color="#198282" />,
  },
  {
    path: "family-export",
    name: "Family Data Export",
    acceptableRoles: [ROLE_ADMIN],
    icon: <FileDown size={32} color="#198282" />,
  },
  // add more tools here
];

const ToolTile = ({
  tool,
  currentUser,
}: {
  tool: Tool;
  currentUser: CurrentUser;
}) => {
  const disabled = !hasOneOfRoles(currentUser, tool.acceptableRoles);
  return (
    /* each item takes up 1/2 (6/12ths) of the row */
    <Grid item xs={12} md={6}>
      <LinkRouter
        sx={{ display: "block", position: "relative" }}
        key={tool.path}
        to={tool.path.startsWith("/") ? tool.path : `/tools/${tool.path}`}
        disabled={disabled}
      >
        {tool.badged && (
          <span
            style={{
              position: "absolute",
              top: -3,
              left: -6,
              backgroundColor: "rgb(211, 47, 47)",
              minWidth: "12px",
              height: "12px",
              borderRadius: "12px",
            }}
          ></span>
        )}
        <Box
          display="flex"
          flexDirection="column"
          gap="8px"
          sx={(theme) => ({
            display: "flex",
            border: `1px solid ${theme.palette.border}`,
            padding: "24px 32px",
            justifyContent: "center",
            cursor: "pointer",
            alignSelf: "stretch",
            borderRadius: "8px",
            background: "#FFF",
            overflow: "hidden",
            ...(disabled && { opacity: 0.5 }),
          })}
        >
          <Box
            display="flex"
            flexDirection="row"
            gap="12px"
            alignItems="center"
          >
            {tool.icon}
            <Typography variant="h4">{tool.name}</Typography>
          </Box>
          {tool.description && (
            <Typography
              variant="body"
              sx={{
                height: "46px",
                overflow: "hidden",
              }}
            >
              {tool.description}
            </Typography>
          )}
        </Box>
      </LinkRouter>
    </Grid>
  );
};

export default () => {
  const isMobile = useIsMobile();
  const currentUser = useContext(CurrentUserContext);

  const advisorTools = tools
    .filter((tool) => tool.acceptableRoles.includes(ROLE_ADVISOR))
    .sort((a, b) => a.name.localeCompare(b.name));
  const adminTools = tools
    .filter((tool) => !tool.acceptableRoles.includes(ROLE_ADVISOR))
    .sort((a, b) => a.name.localeCompare(b.name));
  return (
    <Box
      sx={{
        margin: isMobile ? "" : "auto",
        maxWidth: "1000px",
        padding: isMobile ? "20px" : "64px",
      }}
    >
      <Box display="flex" flexDirection="row">
        <Box
          display="flex"
          flexDirection="column"
          width="100%"
          marginBottom="16px"
        >
          <Breadcrumbs
            breadcrumbs={[
              {
                name: "Home",
                link: "/",
              },
            ]}
          />
          <Box
            display="flex"
            width="100%"
            gap={isMobile ? "12px" : "24px"}
            {...(isMobile && { flexDirection: "column" })}
            {...(!isMobile && {
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            })}
          >
            <Typography variant="display">Tools</Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: "16px",
              alignSelf: "stretch",
              flexDirection: "column",
              marginBottom: "25px",
              marginTop: "38px",
            }}
          >
            {/* spacing for gaps between items. MUI's spacing scale is based on a 8px unit, so 2 would result in a 16px gap */}
            <Grid container spacing={isMobile ? 1 : 3}>
              {advisorTools.map((tool, idx) => (
                <ToolTile key={idx} tool={tool} currentUser={currentUser} />
              ))}
            </Grid>
          </Box>
          {hasOneOfRoles(currentUser, [ROLE_ADMIN, ROLE_SUPERADMIN]) && (
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: "16px",
                alignSelf: "stretch",
                flexDirection: "column",
                marginBottom: "25px",
                marginTop: "38px",
              }}
            >
              <Typography variant="h3">Admin only</Typography>
              {/* spacing for gaps between items. MUI's spacing scale is based on a 8px unit, so 2 would result in a 16px gap */}
              <Grid container spacing={isMobile ? 1 : 3}>
                {adminTools.map((tool, idx) => (
                  <ToolTile key={idx} tool={tool} currentUser={currentUser} />
                ))}
              </Grid>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
